/**
 * ModeToggle Component
 * 
 * Toggle between Live Mode (real-time activity) and Heatmap Mode (historical intensity).
 * Includes time window selector for heatmap mode and persists mode selection to localStorage.
 * 
 * Features:
 * - Visual toggle between Live and Heatmap modes
 * - Time window selector (1h, 4h, 8h, Today) for Heatmap mode
 * - Auto-switching control (pause/resume in Live mode)
 * - Mode persistence to localStorage
 * - Responsive design with icons and labels
 * 
 * @author BizClick WMS
 * @version 1.28 - Fase 6 Implementation
 */

import React, { useEffect, useState } from 'react';
import { Radio, BarChart3, Clock, Pause, Play } from 'lucide-react';
import { TimeWindow } from './ActivityCacheManager';
import * as localStorageHelper from '../../../../utils/localStorageHelper';

interface ModeToggleProps {
  /** Current mode */
  mode: 'live' | 'heatmap';
  
  /** Callback when mode changes */
  onModeChange: (mode: 'live' | 'heatmap') => void;
  
  /** Current time window (for heatmap mode) */
  timeWindow: TimeWindow;
  
  /** Callback when time window changes */
  onTimeWindowChange: (window: TimeWindow) => void;
  
  /** Auto-switching enabled (for live mode) */
  autoSwitchEnabled?: boolean;
  
  /** Callback to toggle auto-switching */
  onAutoSwitchToggle?: () => void;
  
  /** Optional: Disable mode switching */
  disabled?: boolean;
}

/**
 * localStorage key for mode persistence
 */
const MODE_STORAGE_KEY = 'bizclick_dashboard_mode';
const TIME_WINDOW_STORAGE_KEY = 'bizclick_dashboard_timewindow';

/**
 * Time window labels (Norwegian)
 */
const TIME_WINDOW_LABELS: Record<TimeWindow, string> = {
  [TimeWindow.Last1Hour]: '1 time',
  [TimeWindow.Last4Hours]: '4 timer',
  [TimeWindow.Last8Hours]: '8 timer',
  [TimeWindow.Today]: 'I dag'
};

export const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  onModeChange,
  timeWindow,
  onTimeWindowChange,
  autoSwitchEnabled = true,
  onAutoSwitchToggle,
  disabled = false
}) => {
  const [mounted, setMounted] = useState(false);

  // Load persisted mode and time window on mount
  useEffect(() => {
    setMounted(true);
    
    // Restore mode from localStorage
    const savedMode = localStorageHelper.getItem<'live' | 'heatmap'>(MODE_STORAGE_KEY);
    if (savedMode && (savedMode === 'live' || savedMode === 'heatmap')) {
      onModeChange(savedMode);
    }

    // Restore time window from localStorage
    const savedTimeWindow = localStorageHelper.getItem<TimeWindow>(TIME_WINDOW_STORAGE_KEY);
    if (savedTimeWindow && Object.values(TimeWindow).includes(savedTimeWindow)) {
      onTimeWindowChange(savedTimeWindow);
    }
  }, []);

  /**
   * Handle mode change with persistence
   */
  const handleModeChange = (newMode: 'live' | 'heatmap') => {
    if (disabled) return;
    
    onModeChange(newMode);
    
    // Persist to localStorage
    localStorageHelper.setItem(MODE_STORAGE_KEY, newMode);
    
    console.log('[ModeToggle] Mode changed to:', newMode);
  };

  /**
   * Handle time window change with persistence
   */
  const handleTimeWindowChange = (newWindow: TimeWindow) => {
    onTimeWindowChange(newWindow);
    
    // Persist to localStorage
    localStorageHelper.setItem(TIME_WINDOW_STORAGE_KEY, newWindow);
    
    console.log('[ModeToggle] Time window changed to:', newWindow);
  };

  if (!mounted) {
    return null; // Prevent SSR hydration issues
  }

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-sm space-y-3">
      {/* Mode toggle buttons */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Visning:</span>
        
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
          {/* Live mode button */}
          <button
            onClick={() => handleModeChange('live')}
            disabled={disabled}
            className={`
              flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium
              transition-all
              ${mode === 'live'
                ? 'bg-blue-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <Radio className={`w-4 h-4 ${mode === 'live' ? 'animate-pulse' : ''}`} />
            <span>Live</span>
          </button>

          {/* Heatmap mode button */}
          <button
            onClick={() => handleModeChange('heatmap')}
            disabled={disabled}
            className={`
              flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium
              transition-all
              ${mode === 'heatmap'
                ? 'bg-orange-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Heatmap</span>
          </button>
        </div>
      </div>

      {/* Time window selector (only visible in heatmap mode) */}
      {mode === 'heatmap' && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600 flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>Tidsvindu:</span>
            </span>
          </div>
          
          <div className="grid grid-cols-4 gap-1">
            {Object.values(TimeWindow).map((window) => (
              <button
                key={window}
                onClick={() => handleTimeWindowChange(window)}
                className={`
                  px-2 py-1.5 rounded text-xs font-medium
                  transition-colors
                  ${timeWindow === window
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {TIME_WINDOW_LABELS[window]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Auto-switching control (only visible in live mode) */}
      {mode === 'live' && onAutoSwitchToggle && (
        <div className="pt-2 border-t border-gray-200">
          <button
            onClick={onAutoSwitchToggle}
            className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs font-medium text-gray-700">
              Auto-bytte
            </span>
            <div className="flex items-center space-x-2">
              <span className={`text-xs ${autoSwitchEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {autoSwitchEnabled ? 'PÅ' : 'AV'}
              </span>
              {autoSwitchEnabled ? (
                <Play className="w-4 h-4 text-green-600" />
              ) : (
                <Pause className="w-4 h-4 text-gray-500" />
              )}
            </div>
          </button>
        </div>
      )}

      {/* Mode description */}
      <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
        {mode === 'live' ? (
          <div>
            ⚡ <span className="font-medium">Live:</span> Viser sanntidsaktivitet med animasjoner
          </div>
        ) : (
          <div>
            🔥 <span className="font-medium">Heatmap:</span> Viser aktivitetsintensitet over tid
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeToggle;
